//#region header */
/**************************************************************************************************
//
//  Description: Application page which displays the application tree and details.
//
//  Copyright:    © 2026 Idox Software Limited
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//#region Version 1.0.0.0 changes
//    001   05.02.26 Sean Flook         GMSCM-12 Initial Revision.
//#endregion Version 1.0.0.0 changes
//
//--------------------------------------------------------------------------------------------------
//#endregion header */

import React, { useEffect, useRef, useState } from "react";

import useApplicationQueryStore from "../context/ApplicationStore";
import useApplicationNames from "../hooks/useApplicationNames";
import usePostApplicationNames from "../hooks/usePostApplicationNames";
import { validateApplicationData } from "../utils/ApplicationValidation";

import { ApplicationNameDto, ApplicationNamesDataDto } from "../dtos/applicationNamesDto";
import { ApplicationErrorType } from "../models/applicationErrorType";

import { Backdrop, Box, Button, CircularProgress, Grid2, Stack } from "@mui/material";
import ActionDialog, { ActionVariant, DialogMode } from "../dialogs/ActionDialog";

import ApplicationTree from "../forms/ApplicationTree";
import ApplicationForm from "../forms/ApplicationForm";

import SaveIcon from "@mui/icons-material/Save";
import ErrorIcon from "@mui/icons-material/Error";

import { dataFormStyle, getSaveButtonStyle, toolbarStyle } from "../utils/ADSStyles";

interface ApplicationPageProps {}

const ApplicationPage: React.FC<ApplicationPageProps> = () => {
  const { data, error, isLoading } = useApplicationNames();

  const applicationNames = useApplicationQueryStore((s) => s.applicationQuery.applicationNames);
  const validationErrors = useApplicationQueryStore((s) => s.applicationQuery.validationErrors);
  const setApplicationNames = useApplicationQueryStore((s) => s.setApplicationNames);
  const setApplicationChanged = useApplicationQueryStore((s) => s.setApplicationChanged);
  const setNewApplication = useApplicationQueryStore((s) => s.setNewApplication);

  const postApplicationNames = usePostApplicationNames((newData: ApplicationNameDto[]) => {
    setApplicationData(
      newData.map((x, index) => {
        return { id: index, ...x };
      })
    );
    setApplicationNames(newData);
    setApplicationChanged(false);
    setEnableSave(false);
    setNewApplication(false);
    if (selectedItemId !== newSelectedItemId.current) handleItemSelectedEvent(newSelectedItemId.current);
  });

  const [applicationData, setApplicationData] = useState<ApplicationNamesDataDto[]>([]);

  const [errorText, setErrorText] = useState<string | undefined>(undefined);
  const [applicationErrors, setApplicationErrors] = useState<ApplicationErrorType[] | undefined>(undefined);
  const [selectedItemId, setSelectedItemId] = useState<number | undefined>(undefined);
  const [expanded, setExpanded] = useState<number[]>([]);

  const [haveErrors, setHaveErrors] = useState<boolean>(false);
  const [errorIds, setErrorIds] = useState<number[]>([]);
  const [enableSave, setEnableSave] = useState<boolean>(false);

  const [applicationFormData, setApplicationFormData] = useState<ApplicationNamesDataDto | undefined>();

  const newSelectedItemId = useRef<number | undefined>(undefined);
  const deleteId = useRef<number | undefined>(undefined);

  const [openActionDialog, setOpenActionDialog] = useState<boolean>(false);
  const actionData = useRef<string | undefined>(undefined);
  const actionMode = useRef<DialogMode>(DialogMode.delete);
  const actionType = useRef<ActionVariant>(undefined);

  useEffect(() => {
    if (error) setErrorText(error.message);
  }, [error]);

  /**
   * Method to handle the event when the user chooses to save the changes made to the current application.
   */
  const handleSaveClicked = () => {
    if (!applicationData || applicationData.length === 0 || (applicationErrors && applicationErrors.length > 0)) return;
    postApplicationNames.mutate(applicationData);
  };

  /**
   * Event to handle when a node from the tree view is selected.
   *
   * @param nodeId The id of the selected node.
   */
  const handleItemSelectedEvent = (nodeId: number | undefined): void => {
    newSelectedItemId.current = nodeId;
    setSelectedItemId(nodeId);
    const selectedApplication = applicationData.find((app) => app.id === nodeId);
    setApplicationFormData(selectedApplication);
  };

  /**
   * Method to add new items to the application.
   *
   * @param {string} type The type of item to add.
   * @param {string} [id] The id of the currently selected node.
   */
  const handleAddApplication = (): void => {
    const newApplication: ApplicationNamesDataDto = {
      id: applicationData.length, // Generate a unique id
      name: "_New Application",
      dbConnectionNames: [],
      elasticConnectionNames: [],
    };
    setApplicationData((prevState) => [...prevState, newApplication]);
    setApplicationNames([
      ...(applicationNames || []),
      { name: newApplication.name, dbConnectionNames: [], elasticConnectionNames: [] },
    ]);
    newSelectedItemId.current = newApplication.id;
    setSelectedItemId(newApplication.id);
    setApplicationFormData(newApplication);
    setApplicationChanged(true);
    setNewApplication(true);
    setEnableSave(true);
  };

  /**
   * Method to delete an application.
   *
   * @param {number} id The id of the application to delete.
   */
  const deleteApplication = (id: number): void => {
    const updatedApplications = applicationData.filter((app) => app.id !== id);
    const updatedNames: ApplicationNameDto[] = updatedApplications.map((app) => ({
      name: app.name,
      dbConnectionNames: app.dbConnectionNames,
      elasticConnectionNames: app.elasticConnectionNames,
    }));

    if (updatedNames.length === 0) {
      setErrorText("At least one application name is required.");
      setApplicationChanged(false);
      setEnableSave(false);
      window.alert("You cannot delete the last remaining application.");
      return;
    } else {
      setApplicationData(updatedApplications);
      setApplicationNames(updatedNames);
      postApplicationNames.mutate(updatedNames);
    }

    if (selectedItemId === id) {
      setSelectedItemId(undefined);
      setApplicationFormData(undefined);
    }
  };

  const handleDeleteApplication = (id: number): void => {
    deleteApplication(id);
  };

  /**
   * Method to handle the event when the expanded items are changed.
   *
   * @param items The expanded items.
   */
  const handleExpandedItemsChange = (items: number[]): void => {
    setExpanded(items);
  };

  /**
   * Method to handle the event when the application form data is changed.
   *
   * @param {ApplicationNamesDataDto} data The updated application form data.
   */
  const handleApplicationFormDataChange = (data: ApplicationNamesDataDto): void => {
    if (!data || !data.name) return;

    if (applicationData.find((app) => app.id === data.id)) {
      // Updated named settings only
      setApplicationData((prevState) => {
        const updatedState = prevState.map((app) => (app.id === data.id ? data : app));
        return updatedState;
      });
      const updatedNames = (applicationNames || []).map((app) =>
        app.name === applicationData.find((a) => a.id === data.id)?.name
          ? {
              name: data.name,
              dbConnectionNames: data.dbConnectionNames,
              elasticConnectionNames: data.elasticConnectionNames,
            }
          : app
      );
      setApplicationNames(updatedNames);
    } else if (newSelectedItemId.current && applicationData.find((app) => app.id === newSelectedItemId.current)) {
      // Changed application name
      setApplicationData((prevState) => {
        const updatedState = prevState.map((app) => (app.id === newSelectedItemId.current ? data : app));
        return updatedState;
      });
      const updatedNames = (applicationNames || []).map((app) =>
        app.name === applicationData.find((a) => a.id === newSelectedItemId.current)?.name
          ? {
              name: data.name,
              dbConnectionNames: data.dbConnectionNames,
              elasticConnectionNames: data.elasticConnectionNames,
            }
          : app
      );
      setApplicationNames(updatedNames);
      newSelectedItemId.current = data.id;
    } else {
      // New application
      setApplicationData((prevState) => [...prevState, data]);
      setApplicationNames([
        ...(applicationNames || []),
        {
          name: data.name,
          dbConnectionNames: data.dbConnectionNames,
          elasticConnectionNames: data.elasticConnectionNames,
        },
      ]);
      newSelectedItemId.current = data.id;
    }
    setApplicationFormData(data);
    setApplicationChanged(true);
    setEnableSave(true);
  };

  /**
   * Method to handle the edit named setting close event.
   */
  const handleActionDialogClose = (): void => {
    setOpenActionDialog(false);
  };

  /**
   * Method to handle the delete application event.
   */
  const handleActionDialogDelete = (): void => {
    if (deleteId.current === undefined) return;

    const id = deleteId.current;
    deleteId.current = undefined;
    deleteApplication(id);
    setOpenActionDialog(false);
  };

  useEffect(() => {
    if (!isLoading && (!applicationData || applicationData.length === 0)) {
      if (data && data.length > 0) {
        setApplicationData(
          data.map((x, index) => {
            return { id: index, ...x };
          })
        );
        setApplicationNames(data);
        setApplicationChanged(false);
        setEnableSave(false);
        deleteId.current = undefined;
        setApplicationErrors(undefined);
      }
    }
  }, [data, applicationData, isLoading, setApplicationChanged, setApplicationNames]);

  useEffect(() => {
    setHaveErrors(!!errorText || (applicationErrors?.length ?? 0) > 0);

    if (applicationErrors && applicationErrors.length > 0) {
      const ids: number[] = applicationErrors.map((x) => Number(x.id));
      setErrorIds(ids);
    }
  }, [errorText, applicationErrors]);

  useEffect(() => {
    if (applicationData && applicationData.length > 0) {
      const errors = validateApplicationData(applicationData);
      setApplicationErrors(errors);
    }
  }, [applicationData]);

  useEffect(() => {
    setApplicationErrors(validationErrors);
  }, [validationErrors]);

  return isLoading ? (
    <div>
      <Backdrop open={isLoading}>
        Loading data, please wait...
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  ) : (
    <>
      <Box sx={dataFormStyle("ADSApplicationsPageControl")}>
        <Grid2 container justifyContent="flex-start" spacing={0}>
          <Grid2 size={3}>
            <ApplicationTree
              data={applicationData}
              selectedItem={selectedItemId}
              expanded={expanded}
              haveErrors={haveErrors}
              errorIds={errorIds}
              onItemSelected={handleItemSelectedEvent}
              onAddApplication={handleAddApplication}
              onDeleteApplication={handleDeleteApplication}
              onExpandedItemsChange={handleExpandedItemsChange}
            />
          </Grid2>
          <Grid2 size={9}>
            <Box sx={toolbarStyle}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                spacing={1}
                sx={{ ml: "12px", mt: "8px" }}
              >
                <Button
                  id="application-save-button"
                  sx={getSaveButtonStyle(haveErrors)}
                  variant="text"
                  startIcon={!haveErrors ? <SaveIcon /> : <ErrorIcon />}
                  disabled={!enableSave}
                  onClick={handleSaveClicked}
                >
                  Save
                </Button>
              </Stack>
            </Box>
            {applicationFormData && (
              <ApplicationForm
                data={applicationFormData}
                errors={applicationErrors}
                onDataChange={handleApplicationFormDataChange}
              />
            )}
          </Grid2>
        </Grid2>
      </Box>
      <ActionDialog
        open={openActionDialog}
        mode={actionMode.current!}
        variant={actionType.current!}
        data={actionData.current}
        onClose={handleActionDialogClose}
        onDelete={handleActionDialogDelete}
      />
    </>
  );
};

export default ApplicationPage;
